import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, createFileRoute, notFound } from '@tanstack/react-router';
import { hasAnyAdminSection, visibleAdminSections } from '@/features/admin/guard';
import { useSystemWarnings } from '@/features/admin/system/use-system-warnings';
import { adminTexts } from '@/features/admin/texts';
import { featuresQuery } from '@/features/feature-flags/use-feature';
import { cn } from '@/lib/utils';

const labels = {
  '/admin/users': adminTexts.nav.users,
  '/admin/organizations': adminTexts.nav.organizations,
  '/admin/roles': adminTexts.nav.roles,
  '/admin/resources': adminTexts.nav.resources,
  '/admin/job-roles': adminTexts.nav.jobRoles,
  '/admin/skills': adminTexts.nav.skills,
  '/admin/audit': adminTexts.nav.audit,
  '/admin/system': adminTexts.nav.system,
} as const;

/** ADM-01..03, ADM-05, RES-01/02. Visible only with an admin.* permission or job_roles.manage; the API checks every request itself. */
export const Route = createFileRoute('/_app/admin')({
  beforeLoad: async ({ context }) => {
    const { flags } = await context.queryClient.query(featuresQuery);
    if (!hasAnyAdminSection(context.me, flags)) throw notFound();
  },
  component: AdminLayout,
});

function AdminLayout() {
  const { me } = Route.useRouteContext();
  const { data: features } = useQuery(featuresQuery);
  const sections = visibleAdminSections(me, features?.flags);
  const warnings = useSystemWarnings();
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h1 className="text-2xl font-semibold">{adminTexts.title}</h1>
        <nav className="flex gap-1 border-b" aria-label={adminTexts.title}>
          {sections.map((section) => (
            <Link key={section.to} to={section.to}>
              {({ isActive }) => (
                <span
                  className={cn(
                    '-mb-px inline-block border-b-2 px-3 py-2 text-sm',
                    isActive
                      ? 'border-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground border-transparent',
                  )}
                >
                  {labels[section.to]}
                  {section.to === '/admin/system' && warnings > 0 ? (
                    <span
                      className="bg-destructive ml-1.5 inline-flex min-w-5 justify-center rounded-full px-1.5 text-xs font-medium text-white"
                      aria-label={adminTexts.system.warningsLabel(warnings)}
                    >
                      {warnings}
                    </span>
                  ) : null}
                </span>
              )}
            </Link>
          ))}
        </nav>
      </div>
      <Outlet />
    </div>
  );
}
